import React from 'react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import { connect } from 'react-redux'
import {
  activateZendeskIntegrationAndReturnSuccess,
  getZendeskAuthenticationSetting,
} from 'actions'

import AgentAuthenticationModalPresentation from 'components/Admin/Surveys/ZendeskIntegrationSettings/AgentAuthenticationModal'
import { withHelpdeskIntegrationContext } from 'organisms/HelpdeskIntegration/context'
import { toast } from 'libs/toast'
import { connectApi } from 'libs/connectApi'
import { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import generateUserActivityLog from 'libs/generateUserActivityLog'

class AgentAuthenticationModal extends React.Component {
  static propTypes = {
    isFailedAuthenticated: PropTypes.bool,
    activateZendeskIntegration: PropTypes.func,
    zendeskCredential: PropTypes.object,
    getZendeskAuthenticationSetting: PropTypes.func,
    context: PropTypes.object,
    createUserActivityLog: PropTypes.object,
  }

  state = {
    modalOpen: false,
    isLoading: false,
    token: '',
    subdomain: '',
    email: '',
  }

  /**
   *
   * @param {import('hooks/useSaveUserActivityLog').ACTION_NAME} action
   * @param {object} requestBody
   * @param {number} statusCode
   */
  saveUserActivityLog = (action, requestBody, statusCode) => {
    const data = generateUserActivityLog({
      action,
      target: 'zendesk_credential',
      statusCode,
      requestBody,
    })
    this.props.createUserActivityLog.callApi({ data })
  }

  resetZendeskCredentialForm = (zendeskCredential) => {
    const { token, subdomain, email } = zendeskCredential
    this.setState({
      token,
      subdomain,
      email,
    })
  }

  handleOpen = () =>
    this.setState({
      modalOpen: true,
    })

  handleClose = () => {
    this.resetZendeskCredentialForm(this.props.zendeskCredential)
    this.setState({
      modalOpen: false,
    })
  }

  onChangeToken = (event) => {
    this.setState({
      token: event.target.value,
    })
  }

  onChangeSubdomain = (event) => {
    this.setState({
      subdomain: event.target.value,
    })
  }

  onChangeEmail = (event) => {
    this.setState({
      email: event.target.value,
    })
  }

  onSubmit = async () => {
    this.setState({
      isLoading: true,
    })

    const requestPermissionData = {
      integration_type: 'get_ticket_data',
      source: 'zendesk',
      credential: {
        username: `${this.state.email}/token`,
        password: this.state.token,
        subdomain: this.state.subdomain,
      },
    }

    const shouldNotCallActivate = await this.props.checkIntegrationPermission
      .callApi({
        data: requestPermissionData,
      })
      .then((response) => {
        if (response.get_ticket_data !== true) {
          toast.error(
            "Oops! You cannot connect using end-user credentials. Please use an admin's email address."
          )
          return true
        } else {
          toast.success("Success! You've successfully connected with Zendesk.")
          this.saveUserActivityLog(
            ACTION_NAME.authenticate_zendesk_credential,
            { username: this.state.email },
            response.status
          )
          return false
        }
      })
      .catch((error) => {
        let errorMessage = R.pathOr(
          'Oops! Something went wrong. Please contact Simplesat support for help.',
          ['error', 'title'],
          error.data
        )
        if (errorMessage.includes('No help desk')) {
          errorMessage =
            "Uh oh. The subdomain you've entered isn't working. Please make sure you're using the correct one."
        }
        toast.error(errorMessage)
        this.saveUserActivityLog(
          ACTION_NAME.authenticate_zendesk_credential,
          { username: this.state.email },
          error.status
        )
        return true
      })

    if (shouldNotCallActivate) {
      this.setState({
        isLoading: false,
      })
      return
    }

    this.promise = this.props
      .activateZendeskIntegration(this.state.token, this.state.email, this.state.subdomain)
      .then((isSuccess) => {
        this.setState({
          isLoading: false,
        })
        return (
          isSuccess &&
          this.setState({
            modalOpen: false,
          })
        )
      })
  }

  componentDidMount() {
    this.props.getZendeskAuthenticationSetting()
    this.resetZendeskCredentialForm(this.props.zendeskCredential)
  }

  componentWillUnmount() {
    if (this.promise) {
      this.promise.cancel()
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.zendeskCredential !== this.props.zendeskCredential) {
      this.resetZendeskCredentialForm(this.props.zendeskCredential)
    }
  }

  AuthenticationFailedWarning = () => (
    <div className="ui negative message">
      <div className="content">
        <div className="header">Authentication Unsuccessful</div>
        <p>Please double check your credentials and try again.</p>
      </div>
    </div>
  )

  render() {
    return (
      <AgentAuthenticationModalPresentation
        token={this.state.token}
        email={this.state.email}
        subdomain={this.state.subdomain}
        onChangeToken={this.onChangeToken}
        onChangeEmail={this.onChangeEmail}
        onChangeSubdomain={this.onChangeSubdomain}
        onSubmit={this.onSubmit}
        isFailedAuthenticated={this.props.isFailedAuthenticated}
        modalOpen={this.state.modalOpen}
        handleClose={this.handleClose}
        handleOpen={this.handleOpen}
        isLoading={this.state.isLoading}
      />
    )
  }
}

const mapState = ({ data, pages }) => ({
  zendeskCredential: data.integrations.zendesk.authentication,
  isFailedAuthenticated: pages.integrations.zendesk.isFailedAuthenticated,
})

const mapDispatch = (dispatch, props) => {
  const { surveyId } = props
  return {
    getZendeskAuthenticationSetting: () => dispatch(getZendeskAuthenticationSetting()),
    activateZendeskIntegration: (token, email, subdomain) =>
      dispatch(activateZendeskIntegrationAndReturnSuccess(token, email, subdomain, surveyId)),
  }
}

export default R.compose(
  connectApi({
    name: 'checkIntegrationPermission',
    url: 'survey/check-integration-permission/',
    method: 'post',
    skip: true,
  }),
  connectApi({
    name: 'createUserActivityLog',
    url: `/user-activity-log/write/`,
    method: 'post',
    skip: true,
  }),
  connect(mapState, mapDispatch),
  withHelpdeskIntegrationContext
)(AgentAuthenticationModal)



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/containers/Admin/Surveys/ZendeskIntegrationSettings/AgentAuthenticationModal.js